<template>
  <div>
    <h3>Dependencies</h3>
    <div class="row">
      <div class="col-3" v-if="repositories.length > 0">
        <repository-selector :repositories="repositories" v-model:selected="selectedRepositories" />
      </div>
      <div class="col-3" v-if="selectedRepositories.length > 0">
        <branch-selector
          :selectedRepositories="selectedRepositories"
          :repositories="repositories"
          :repositoryBranches="repositoryBranches"
          :initialSelectedBranches="selectedBranchesByRepo"
          @branchChange="onBranchChange"
        />
      </div>
      <div :class="selectedRepositories.length > 0 ? 'col-2' : 'col-3'">
        <select class="form-select" v-model="languageFilter">
          <option value="">All languages</option>
          <option value="ruby">Ruby</option>
          <option value="javascript">JavaScript</option>
        </select>
      </div>
      <div :class="selectedRepositories.length > 0 ? 'col-2' : 'col-3'">
        <select class="form-select" v-model="versionFilter">
          <option value="">All versions</option>
          <option value="uptodate">Only up-to-date</option>
          <option value="outdated">Only outdated</option>
          <option value="major">New major (x.0.0)</option>
          <option value="minor">New minor (0.x.0)</option>
          <option value="patch">New patch (0.0.x*)</option>
        </select>
      </div>
      <div :class="selectedRepositories.length > 0 ? 'col-2' : 'col-3'">
        <select class="form-select" v-model="securityFilter">
          <option value="">All packages</option>
          <option value="only">Packages with security warnings</option>
          <option value="except">Packages without security warnings</option>
        </select>
      </div>
    </div>

    <div v-if="isLoading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else class="card-group my-3">
      <div class="card">
        <div class="card-body d-flex flex-column justify-content-between gap-3 text-center">
          <div>Security warnings</div>
          <div class="audit-stat text-danger">{{ auditStats.filteredHasSecurityWarning }}</div>
        </div>
      </div>
      <div class="card">
        <div class="card-body d-flex flex-column justify-content-between gap-3 text-center">
          <div>Outdated</div>
          <div class="audit-stat text-warning">{{ auditStats.filteredOutdated }}</div>
        </div>
      </div>
      <div class="card">
        <div class="card-body d-flex flex-column justify-content-between gap-3 text-center">
          <div>Up-to-date</div>
          <div class="audit-stat text-success">{{ auditStats.filteredUpToDate }}</div>
        </div>
      </div>
      <div class="card">
        <div class="card-body d-flex flex-column justify-content-between gap-3 text-center">
          <div>Dependencies</div>
          <div class="audit-stat">{{ auditStats.filteredCount }}</div>
        </div>
      </div>
      <div class="card">
        <div class="card-body d-flex flex-column justify-content-between gap-3 bg-dark-subtle text-center">
          <div>Total score</div>
          <div class="audit-stat">{{ auditStats.scorePercent }}</div>
        </div>
      </div>
    </div>

    <template v-if="!isLoading">
      <div v-if="dependenciesFiltered.length === 0" class="alert alert-info my-3">
        No dependencies found. Go to the repository page to sync branches, create builds and fetch dependencies.
      </div>
      <table v-else class="table" style="table-layout: fixed">
        <thead>
          <tr>
            <th width="15%" v-if="repositories.length > 0">Repository</th>
            <th width="10%">Language</th>
            <th width="15%">Name</th>
            <th width="15%">Current version</th>
            <th width="15%">Latest version</th>
            <th width="30%">Security warnings</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dependency, idx) in dependenciesFiltered"
            :key="idx"
            :class="{
              'table-warning': dependency.meta.outdated,
              'table-danger': dependency.meta.hasSecurityWarnings,
              'table-success': dependency.meta.upToDate
            }"
          >
            <td style="word-wrap: break-word" v-if="repositories.length > 0">
              {{ dependency.build?.repository?.title }}
            </td>
            <td style="word-wrap: break-word">{{ dependency.language }}</td>
            <td style="word-wrap: break-word">{{ dependency.name }}</td>
            <td style="word-wrap: break-word">{{ dependency.current_version }}</td>
            <td style="word-wrap: break-word">{{ dependency.latest_version }}</td>
            <td>
              <div v-if="securityFilter == 'only'" class="security-warning" v-text="dependency.security_warnings"></div>
              <div v-else-if="dependency.security_warnings">Use package filter to show security warnings</div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import RepositorySelector from "./RepositorySelector.vue";
import BranchSelector from "./BranchSelector.vue";
import { compareSemVer, isValidSemVer, parseSemVer } from "semver-parser";

const props = defineProps({
  dependencies: Array,
  repositories: Array,
  repositoryGroups: Array,
  availableBranches: Array,
  selectedBranchesByRepo: Object,
  repositoryBranches: Object,
  initialSelectedRepositories: Array
});

const languageFilter = ref("");
const versionFilter = ref("");
const securityFilter = ref("");
const selectedRepositories = ref([...props.initialSelectedRepositories]);
const dependencies = ref([...props.dependencies]);
const selectedBranchesByRepo = ref({ ...props.selectedBranchesByRepo });
const isLoading = ref(false);

watch(selectedRepositories, (newRepos, oldRepos) => {
  const removedRepos = oldRepos.filter((origin) => !newRepos.includes(origin));

  removedRepos.forEach((repoOrigin) => {
    delete selectedBranchesByRepo.value[repoOrigin];
  });

  if (removedRepos.length > 0) {
    fetchDependencies(selectedBranchesByRepo.value);
  }
});

onMounted(() => {
  fetchDependencies(selectedBranchesByRepo.value);
});

async function fetchDependencies(branchesByRepo) {
  isLoading.value = true;

  try {
    const params = new URLSearchParams();

    selectedRepositories.value.forEach((repoOrigin) => {
      params.append("repositories[]", repoOrigin);
    });

    Object.entries(branchesByRepo).forEach(([repoOrigin, branch]) => {
      params.append(`branches[${repoOrigin}]`, branch);
    });

    window.history.pushState({}, "", `/?${params.toString()}`);

    const response = await fetch(`/filter_dependencies?${params.toString()}`);
    const data = await response.json();

    dependencies.value = data.dependencies;
    selectedBranchesByRepo.value = data.selected_branches_by_repo;
  } catch (error) {
    console.error("Failed to fetch dependencies:", error);
  } finally {
    isLoading.value = false;
  }
}

function onBranchChange(branchesByRepo) {
  fetchDependencies(branchesByRepo);
}

function rubyGemToSemVer(version) {
  return version.replace(/\.([a-zA-Z]+)/g, "-$1").replace(/([0-9]+)\.([0-9]+)\.([0-9]+)\.([0-9]+)/, "$1.$2.$3+$4");
}

const dependenciesMapped = computed(() => {
  return dependencies.value.map((dependency) => {
    const currentVersion = rubyGemToSemVer(dependency.current_version);
    const latestVersion = rubyGemToSemVer(dependency.latest_version);
    const hasSecurityWarnings = dependency.security_warnings !== null && dependency.security_warnings.length > 0;

    if (!isValidSemVer(currentVersion) || !isValidSemVer(latestVersion)) {
      const outdated = currentVersion !== latestVersion;
      return {
        ...dependency,
        meta: {
          outdated,
          upToDate: !outdated && !hasSecurityWarnings,
          hasSecurityWarnings,
          outdatedVersionParts: outdated ? ["major", "minor", "patch"] : []
        }
      };
    }

    const currentParsed = parseSemVer(currentVersion);
    const latestParsed = parseSemVer(latestVersion);
    let outdatedVersionParts = [];

    if (latestParsed.major > currentParsed.major) {
      outdatedVersionParts.push("major");
    } else if (latestParsed.minor > currentParsed.minor) {
      outdatedVersionParts.push("minor");
    } else if (latestParsed.patch > currentParsed.patch) {
      outdatedVersionParts.push("patch");
    } else if ((latestParsed.build ?? []).join(",") !== (currentParsed.build ?? []).join(",")) {
      outdatedVersionParts.push("patch");
    }

    if (compareSemVer(currentVersion, latestVersion) === 1) {
      outdatedVersionParts = [];
    }

    const outdated = outdatedVersionParts.length > 0;

    return {
      ...dependency,
      meta: {
        outdated,
        upToDate: !outdated && !hasSecurityWarnings,
        hasSecurityWarnings,
        outdatedVersionParts
      }
    };
  });
});

const dependenciesFiltered = computed(() => {
  return dependenciesMapped.value
    .filter((dependency) => {
      if (selectedRepositories.value.length === 0) {
        return true;
      }

      const repositoryOrigin = dependency.build?.repository?.origin;
      if (!repositoryOrigin) {
        return false;
      }

      return selectedRepositories.value.includes(repositoryOrigin);
    })
    .filter((dependency) => {
      if (languageFilter.value !== "" && dependency.language !== languageFilter.value) {
        return false;
      }

      if (versionFilter.value !== "") {
        if (versionFilter.value === "uptodate" && dependency.meta.outdated) {
          return false;
        } else if (versionFilter.value === "outdated" && !dependency.meta.outdated) {
          return false;
        }

        if (versionFilter.value === "major" && !dependency.meta.outdatedVersionParts.includes("major")) {
          return false;
        } else if (versionFilter.value === "minor" && !dependency.meta.outdatedVersionParts.includes("minor")) {
          return false;
        } else if (versionFilter.value === "patch" && !dependency.meta.outdatedVersionParts.includes("patch")) {
          return false;
        }
      }

      if (
        securityFilter.value !== "" &&
        ((dependency.security_warnings === null && securityFilter.value === "only") ||
          (dependency.security_warnings !== null && securityFilter.value === "except"))
      ) {
        return false;
      }

      return true;
    });
});

const auditStats = computed(() => {
  const filteredDeps = dependenciesFiltered.value;
  const count = filteredDeps.length;
  const upToDateCount = filteredDeps.filter((d) => d.meta.upToDate).length;
  const scorePercent =
    count > 0
      ? new Intl.NumberFormat("da-DK", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format((upToDateCount / count) * 100) + "%"
      : "0%";

  const filteredOutdated = filteredDeps.filter((d) => d.meta.outdated).length;
  const filteredHasSecurityWarning = filteredDeps.filter((d) => d.meta.hasSecurityWarnings).length;
  const filteredUpToDate = filteredDeps.filter((d) => d.meta.upToDate).length;

  return {
    scorePercent,
    filteredCount: count,
    filteredOutdated,
    filteredHasSecurityWarning,
    filteredUpToDate
  };
});
</script>

<style>
[v-cloak] {
  display: none;
}

.audit-stat {
  font-size: 3rem;
  font-weight: bold;
}

.security-warning {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
